import * as PIXI from 'pixi.js';
import { YuMode } from '../components/ModeSelector';

interface Particle {
    sprite: PIXI.Text;
    vx: number;
    vy: number;
    rotationSpeed: number;
    life: number;
    active: boolean;
}

export class EmojiBurstSystem {
    private container: PIXI.Container;
    private particles: Particle[] = [];
    private pool: PIXI.Text[] = [];

    private emojiSets: Record<YuMode, string[]> = {
        'curious': ['📸', '⚡', '✨', '💡'],
        'fruity': ['🍎', '🍌', '🍇', '🍊', '🍓', '🍑', '🍒', '🥝'],
        'melancholic': ['🌧️', '❄️', '🌙', '🌊', '🌬️', '⚡', '💧'],
        'mysterious': ['💦', '🍆', '👄', '💋', '🌺', '🍑', '🥀']
    };

    constructor(stage: PIXI.Container) {
        this.container = new PIXI.Container();
        stage.addChild(this.container);
    }

    /**
     * Trigger an emoji burst from the center
     */
    public trigger(mode: YuMode, x: number, y: number): void {
        const emojis = this.emojiSets[mode];
        // Single emoji spawn
        const emoji = emojis[Math.floor(Math.random() * emojis.length)];
        this.spawnParticle(emoji, x, y);
    }

    /**
     * Spawn a single particle
     */
    private spawnParticle(emoji: string, x: number, y: number): void {
        let sprite: PIXI.Text;

        // Get from pool or create new
        if (this.pool.length > 0) {
            sprite = this.pool.pop()!;
            sprite.text = emoji;
            sprite.visible = true;
            sprite.alpha = 1;
            sprite.scale.set(1);
        } else {
            sprite = new PIXI.Text(emoji, {
                fontSize: 40, // Reduced size
                stroke: '#ffffff', // White outline
                strokeThickness: 4,
                dropShadow: true,
                dropShadowColor: '#000000',
                dropShadowBlur: 4,
                dropShadowDistance: 2
            });
            sprite.anchor.set(0.5);
            this.container.addChild(sprite);
        }

        sprite.x = x;
        sprite.y = y;

        // Random velocity outward (Drifting)
        const angle = Math.random() * Math.PI * 2;
        const speed = 1 + Math.random() * 3; // Slower drift speed (1-4)

        this.particles.push({
            sprite,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            rotationSpeed: (Math.random() - 0.5) * 0.05, // Very slow rotation
            life: 1.0,
            active: true
        });
    }

    /**
     * Update all particles
     */
    public update(): void {
        for (let i = this.particles.length - 1; i >= 0; i--) {
            const p = this.particles[i];

            if (!p.active) continue;

            // Physics
            p.sprite.x += p.vx;
            p.sprite.y += p.vy;
            p.sprite.rotation += p.rotationSpeed;

            // Zero Gravity - No vertical acceleration

            // Fade out (Slower)
            p.life -= 0.008; // Very slow fade (lasts ~2s at 60fps)
            p.sprite.alpha = Math.min(p.life * 2, 1); // Stay visible longer

            // Kill if dead
            if (p.life <= 0) {
                this.recycleParticle(i);
            }
        }
    }

    /**
     * Recycle particle back to pool
     */
    private recycleParticle(index: number): void {
        const p = this.particles[index];
        p.sprite.visible = false;
        this.pool.push(p.sprite);
        this.particles.splice(index, 1);
    }

    /**
     * Clean up
     */
    public dispose(): void {
        this.container.destroy({ children: true });
        this.particles = [];
        this.pool = [];
    }
}
